import React, { useMemo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import type { MustDoActivityItem } from 'tripPlanner/hooks/api/mustDoActivities'
import ImageTileCard from './Common/Tile/ImageTileCard'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import * as Analytics from 'analytics/analytics'

interface Props {
  hotelCity: string
  showLocationInHeading?: boolean
  mustDoActivities: Array<MustDoActivityItem>
  selectedMustDoActivityNames: Array<string>
  onMustDoActivityToggle: (activity: string) => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripMustDoSelection(props: Props) {
  const {
    hotelCity,
    showLocationInHeading = false,
    mustDoActivities,
    selectedMustDoActivityNames,
    onMustDoActivityToggle,
    isGeneratingTrip,
    stepNumber,
    totalSteps,
  } = props
  const intl = useIntl()

  const selectedMustDoSet = useMemo(() =>
    new Set(selectedMustDoActivityNames),
  [selectedMustDoActivityNames])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'must_do_activities',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const stepText = intl.formatMessage(
    {
      id: 'ewX/UMLdx4',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = showLocationInHeading
    ? intl.formatMessage(
        {
          id: 'gcrnPaHxWY',
          defaultMessage: 'Must-do Picks for <i>{hotelCity}</i>',
          description: 'Heading text for must-do activities with italicized city name',
        },
        {
          hotelCity,
          i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
        },
      )
    : intl.formatMessage({
        id: 'IXj1IQB9v5',
        defaultMessage: 'Must-do Picks',
        description: 'Heading text for must-do activities',
      })

  const descriptionText = intl.formatMessage({
    id: 'WW2eVwPNz3',
    defaultMessage: 'Select activities you would like to include in your trip',
    description: 'Description text encouraging users to select activities',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </VerticalSpacer>

      <TileCardsGroup>
        {mustDoActivities.map((item: MustDoActivityItem) =>
          (<ImageTileCard
            key={item.activity}
            title={item.activity}
            subtitle={item.description}
            imageId={item.imageId}
            imageUrl={item.imageUrl}
            value={item.activity}
            onClick={onMustDoActivityToggle}
            selected={selectedMustDoSet.has(item.activity)}
            disabled={isGeneratingTrip}
          />),
        )}
      </TileCardsGroup>
    </Group>
  )
}

export default AITripMustDoSelection
